<script lang="tsx">
  import { computed, defineAsyncComponent, reactive, ref, toRaw, useId, type PropType } from "vue";
  import { Stepper, StepList, StepPanel, StepPanels, Button, Step, ButtonGroup, Message } from "primevue";
  import { Form, type FormInstance } from "@primevue/forms";
  const FormField = defineAsyncComponent(async () => await import("./FormField.vue"));
  import Prose from "./Prose.vue";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowLeft, faArrowRight, faCircleCheck, faCircleNotch, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";

  import { useQuery } from "@urql/vue";
  import { graphql as gql } from "@/gql";
  import type { form_Entry, FreeformField_Html, FreeformField_RichText,
    FreeformFieldInterface, FreeformFormInterface, FreeformPageInterface,
    FreeformRuleConditionInterface, Scalars
  } from "@/gql/graphql";


  export default {
    components: {
      Icon, Message,
      Button, ButtonGroup,
      Prose,
      Stepper, Step, StepList, StepPanel, StepPanels,
      Form, FormField
    },
    props: {
      form: {
        type: [Number, String, Object] as PropType<Scalars["Int"]["input"] | string | FreeformFieldInterface>,
        required: true,
      },
      site: {
        type: String,
        required: true,
      },
      initialValues: {
        type: Object as PropType<Record<string, any>>,
      },
    },
    emits: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      submitted(payload: null) { return true }
    },
    async setup(props) {
      // This needs access to the component instance, which is only available
      // while we are still in the sync context of setup.
      // (so does useQuery, but useId isn't async so it doesn't matter)
      const vid = useId();

      // fixme: support site translations when it's added in freeform!
      const { data } = await useQuery({
        query: gql(`
          query FetchFormFieldsQuery($id: Int, $site: String) {
            freeform {
              form(id: [$id], siteHandles: [$site]) {
                id
                uid
                name
                description
                hash
                enctype
                submissionMutationName
                csrfToken {
                  name
                  value
                }
                honeypot {
                  name
                }
                javascriptTest {
                  name
                }
                successMessage
                successBehavior
                returnUrl

                pages {
                  index
                  label

                  buttons {
                    layout
                    submitLabel
                    back
                    backLabel
                    save
                    saveLabel
                    saveRedirectUrl
                  }

                  rows {
                    fields {
                      ...FieldFragment
                    }
                  }
                }

                hiddenFields: fields {
                  __typename
                  ... on FreeformField_Hidden {
                    ...FieldFragment
                  }
                }

                rules {
                  pages {
                    page {
                      index
                    }
                    combinator
                    conditions {
                      field {
                        handle
                      }
                      operator
                      value
                    }
                  }

                  fields {
                    field {
                      handle
                    }
                    display
                    combinator
                    conditions {
                      field {
                        handle
                      }
                      operator
                      value
                    }
                  }
                }
              }
            }
          }

          fragment FieldFragment on FreeformFieldInterface {
            type
            handle
            pageIndex
            label
            instructions
            required
            requiredMessage

            ... on FreeformField_Text {
              value
              placeholder
              maxLength
            }
            ... on FreeformField_Email {
              value
              placeholder
              maxLength
            }
            ... on FreeformField_Phone {
              value
              placeholder
              maxLength
              pattern  # will probably ignore this
            }
            ... on FreeformField_Regex {
              value
              pattern
              maxLength
              message
            }
            ... on FreeformField_Website {
              value
              placeholder
              maxLength
            }
            ... on FreeformField_Password {
              value
              placeholder
              minLength
              maxLength
              attributes { input { attribute, value }}
            }
            ... on FreeformField_CreditCard {
              value
            }
            ... on FreeformField_Number {
              value
              placeholder
              minValue
              maxValue
              maxLength
              step
              allowNegative
              decimalCount
              # bug: minLength and separators are buggy in graphql
            }
            ... on FreeformField_Datetime {
              dateTimeType
              value
              placeholder
              generatePlaceholder
              maxLength
              minDate
              maxDate
              # ignored a lot of stupid props, will just use browser locale
            }

            ... on FreeformField_Dropdown {
              value
              options {
                label
                value
              }
            }
            ... on FreeformField_MultipleSelect {
              values
              options {
                label
                value
              }
            }

            ... on FreeformField_File {
              fileKinds
              fileCount
              maxFileSizeKB
            }
            # dnd can be queried, but isn't present in the mutation :(
            # ... on FreeformField_FileDnd {
            #   fileKinds
            #   fileCount
            #   maxFileSizeKB
            #   placeholder
            #   # colors are ignored
            # }

            ... on FreeformField_Textarea {
              value
              rows
              placeholder
              maxLength
            }

            ... on FreeformField_Checkbox {
              value
              checkedByDefault
            }
            ... on FreeformField_Checkboxes {
              values
              options {
                label
                value
              }
              oneLine
            }
            ... on FreeformField_Radios {
              value
              options {
                label
                value
              }
              oneLine
            }
            ... on FreeformField_Rating {
              value
              maxValue
              # color props ignored
            }
            ... on FreeformField_OpinionScale {
              value
              scales {
                value
                label
              }
              legends
            }

            ... on FreeformField_Hidden {
              value
              maxLength
            }
            ... on FreeformField_Invisible {
              value
              maxLength
            }

            ... on FreeformField_Html {
              content
            }
            ... on FreeformField_RichText {
              content
            }

            # ... on FreeformField_Table {}
            # ... on FreeformField_Group {}
            # ... on FreeformField_Confirm {}
            # ... on FreeformField_Signature {}
            # ... on FreeformField_Calculation {}
          }
        `),
        variables: {
          id: typeof props.form === "number" ? props.form : parseInt(props.form.toString()),
          site: props.site,
        },
        pause: () => typeof(props.form) === "object",
      });

      return {
        vid,
        data: reactive(computed((): FreeformFormInterface & {hiddenFields: FreeformFieldInterface[]} => {
          if (typeof(props.form) === "object") {
            return props.form as FreeformFormInterface & {hiddenFields: FreeformFieldInterface[]};
          }
          const form_data = data.value!.freeform!.form!;

          const form_data_copy = structuredClone(toRaw(form_data));
          // fixup this query since it also returns a bunch of empty objects
          // (one per non-hidden field)
          form_data_copy.hiddenFields = form_data_copy.hiddenFields!.filter(
            obj => !(Object.keys(obj!).length === 0 || obj!.__typename != "FreeformField_Hidden")
          );

          return form_data_copy as FreeformFormInterface & {hiddenFields: FreeformFieldInterface[]};
        })),
        formComponent: ref(undefined as FormInstance | undefined),
      };
    },
    data() { return {
      faArrowLeft, faArrowRight, faFloppyDisk, faCircleNotch, faCircleCheck,
      currentPage: 0,
      successPage: false,
      submitting: false,
      submitErrors: [] as string[],
    }},
    computed: {
      _form() { return this.data!; },
      states() { return this.formComponent?.states ?? {}; },
      valid() { return this.formComponent?.valid ?? false; },

      enabledPages() {
        const pages = this._form.pages!.filter(page => this.isPageEnabled(page!.index!));
        pages.sort((a, b) => a!.index! - b!.index!);
        return pages as FreeformPageInterface[];
      },
      isFirstPage() {
        return this.enabledPages.findIndex(
          page => page!.index! == this.currentPage
        ) == 0;
      },
      isLastPage() {
        return this.enabledPages.findIndex(
          page => page!.index! == this.currentPage
        ) == this.enabledPages.length - 1;
      },

      pageHasErrors() {
        for (const page of this.enabledPages) {
          if (page.index! <= this.currentPage) {
            for (const row of page!.rows!) {
              for (const field of row!.fields!) {
                if (this.isFieldEnabled(field!, page!.index!)) {
                  if (this.states[field!.handle!]?.invalid ?? false) {
                    return true;
                  }
                }
              }
            }
          }
        }
        return false;
      },
    },
    methods: {
      pageEntries: function*() {
        for (const page of this._form.pages!) {
          yield { page: page! };
        }
      },
      rowFieldEntries: function*(page: FreeformPageInterface) {
        for (const [row_i, row] of page.rows!.entries()) {
          for (const [column_i, field] of row!.fields!.entries()) {
            yield {
              row: row!, field: field!, column_i: column_i, row_i: row_i,
            };
          }
        }
        for (const field of this._form.hiddenFields ?? []) {
          if (field.pageIndex! == page.index!) {
            yield { row: null, field: field!, column_i: null, row_i: null };
          }
        }
      },
      fieldEntries: function*() {
        for (const page of this._form.pages!) {
          for (const row of page!.rows!) {
            for (const field of row!.fields!) {
              yield { page: page!, row: row!, field: field! };
            }
          }
        }
        for (const field of this._form.hiddenFields ?? []) {
          yield { page: this._form.pages![field.pageIndex!], row: null, field: field! }
        }
      },

      isPageEnabled(page: number) {
        if (this._form.rules?.pages == null) { return true; }
        const rule = this._form.rules.pages.find(rule => rule!.page!.index == page);
        if (rule == null) { return true; }

        const result = this.evaluateConditions(
          rule.conditions as FreeformRuleConditionInterface[],
          rule.combinator!
        );
        return result;
      },

      isFieldEnabled(field: FreeformFieldInterface, page: number) {
        if (!this.isPageEnabled(page)) { return false; }

        const rule = this._form.rules?.fields
          ?.find(rule => rule?.field?.handle == field.handle!);
        if (rule == null) { return true; }

        const result = this.evaluateConditions(
          rule.conditions as FreeformRuleConditionInterface[],
          rule.combinator!
        );
        return (rule.display === "show") === result;
      },

      evaluateConditions(conditions: FreeformRuleConditionInterface[], combinator: string) {
        if (combinator === "and") {
          for (const condition of conditions) {
            if (!this.evaluateCondition(condition)) { return false; }
          }
          return true;
        } else if (combinator === "or") {
          for (const condition of conditions) {
            if (this.evaluateCondition(condition)) { return true; }
          }
          return false;
        }
        throw new Error("invalid combinator");
      },

      evaluateCondition(condition: FreeformRuleConditionInterface) {
        if (this.states == undefined || this.states[condition.field!.handle!] == null) { return false; }

        const state: string | string[] | boolean | number = this.states[condition.field!.handle!].value;
        const state_arr = Array.isArray(state) ? state
          : (state != null ? [state] : []);
        const state_str = state?.toString() ?? "";
        const state_num = typeof state === "number" ? state : parseFloat(state_str);

        let condition_arr: string[];
        try {
          condition_arr = JSON.parse(condition.value!) as string[];
        } catch {
          condition_arr = [condition.value!];
        }

        switch (condition.operator!) {
          case "equals":
            if (Array.isArray(state)) {
              return state.length === 1 && state[0] === condition.value!;
            } else {
              return state === condition.value!;
            }
          case "notEquals":
            if (Array.isArray(state)) {
              return !(state.length === 1 && state[0] === condition.value!);
            } else {
              return state !== condition.value!;
            }
          case "greaterThan": return state_num > parseFloat(condition.value!);
          case "greaterThanOrEquals": return state_num >= parseFloat(condition.value!);
          case "lessThan": return state_num < parseFloat(condition.value!);
          case "lessThanOrEquals": return state_num <= parseFloat(condition.value!);
          case "contains": return state_arr.includes(condition.value!);
          case "notContains": return !(state_arr.includes(condition.value!));
          case "startsWith": return state_str.startsWith(condition.value!);
          case "endsWith": return state_str.endsWith(condition.value!);
          case "isEmpty": return state === "" || state == null || (state as any)?.length === 0 || state === false;
          case "isNotEmpty": return !(state === "" || state == null);
          case "isOneOf":
            return condition_arr.includes(state_str);
          case "isNotOneOf":
            return !condition_arr.includes(state_str);
          default: throw new Error("invalid operator");
        }
      },

      // Validate all enabled pages of the form up to the current page.
      async validate() {
        const fields = [];
        for (const page of this.enabledPages) {
          if (page.index! <= this.currentPage) {
            for (const row of page!.rows!) {
              for (const field of row!.fields!) {
                if (this.isFieldEnabled(field!, page!.index!)) {
                  fields.push(field!.handle!);
                }
              }
            }
          }
        }

        const result = await this.formComponent!.validate(fields);
        for (const handle in result.errors ?? {}) {
          if (result.errors[handle].length > 0) {
            return false;
          }
        }
        return true;
      },

      async submit() {
        if (await this.validate() && this.isLastPage) {
          this.$data.submitting = true;
          this.$data.submitErrors.splice(0);

          let args = `
            $csrfToken: FreeformCsrfTokenInputType
            $honeypot: FreeformHoneypotInputType
          `;

          let params = `
            csrfToken: $csrfToken
            honeypot: $honeypot
          `;

          const variables: Record<string, any> = {
            csrfToken: {
              name: this._form.csrfToken?.name,
              value: this._form.csrfToken?.value,
            },
            honeypot: this._form.honeypot != null
              ? { name: this._form.honeypot?.name, value: "" } : null,
            javascriptTest: this._form.javascriptTest != null
              ? { name: this._form.javascriptTest?.name, value: "" } : null,
          };

          for (const {page, field} of this.fieldEntries()) {
            if (this.isFieldEnabled(field!, page!.index!)) {
              // these have no value to submit
              if (["rich-text", "html"].includes(field!.type!)) {
                continue;
              }

              const value = this.states[field!.handle!].value;

              params += `${field!.handle!}: $${field!.handle!}\n`;

              if ([
                "text", "email", "phone", "regex", "website", "password", "creditcard",
                "dropdown", "textarea", "radios",
                "opinion-scale",
                "hidden", "invisible",
              ].includes(field!.type!)) {
                args += `$${field!.handle!}: String\n`;
                variables[field!.handle!] = value;
              } else if (field!.type! === "number") {
                args += `$${field!.handle!}: Number\n`;
                variables[field!.handle!] = value;
              } else if (field!.type! === "datetime") {
                args += `$${field!.handle!}: DateTime\n`;
                variables[field!.handle!] = (value as Date)?.toISOString();
              } else if (field!.type! === "multiple-select") {
                args += `$${field!.handle!}: [String]\n`;
                variables[field!.handle!] = value;
              } else if (field!.type! === "checkbox") {
                args += `$${field!.handle!}: String\n`;
                variables[field!.handle!] = value ? "yes" : "";
              } else if (field!.type! === "checkboxes") {
                args += `$${field!.handle!}: [String]\n`;
                variables[field!.handle!] = value;
              } else if (field!.type! === "rating") {
                args += `$${field!.handle!}: Int\n`;
                variables[field!.handle!] = value;
              } else if (field!.type! === "file") {
                args += `$${field!.handle!}: [FreeformFileUploadInputType]\n`;
                variables[field!.handle!] = [];

                const reader = new FileReader();
                for (const file of value as File[]) {
                  // Despite freeform's own graphql documentation, fileData
                  // is expected to be in a base64 encoded url, *not*
                  // strictly in base64...?
                  const url = await new Promise<string>(resolve => {
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(file);
                  });

                  // Freeform only accepts files with extensions, so if an
                  // extension-less file is provided, let it have an auto-
                  // generated name.
                  const filename = file.name.includes(".") ? file.name : undefined;

                  variables[field!.handle!].push({
                    fileData: url,
                    filename: filename
                  });
                }
              }
            }
          }


          const query = `mutation PostFormSubmission(
            ${args}
          ) {
            ${this._form.submissionMutationName!}(
              ${params}
            ) { success }
          }`;

          try {
            const { data, error } = await this.$gql.mutation(query, variables);
            if (data[this._form.submissionMutationName!]?.success === true) {
              this.successPage = true;
              if (this._form.successBehavior === "redirect-return-url") {
                location.href = this._form.returnUrl!;
              }
            } else {
              const errors = this.$data.submitErrors;

              if (error?.networkError != undefined) {
                errors.push(error.networkError.message);
              }

              for (const gqlError of error?.graphQLErrors ?? []) {
                try {
                  const formErrors = JSON.parse(gqlError.message) as (Record<string, string[]> | string[])[];

                  for (const errorSet of formErrors) {
                    if (Array.isArray(errorSet)) {
                      for (const error of errorSet) {
                        errors.push(error);
                      }
                    } else {  // Record<string, string[]>
                      for (const [handle, handleErrorSet] of Object.entries(errorSet)) {
                        let handleName = handle;
                        // Use the field's pretty name if we know about it
                        for (const {field} of this.fieldEntries()) {
                          if (field.handle === handle) {
                            handleName = field.label!;
                            break;
                          }
                        }

                        for (const error of handleErrorSet) {
                          errors.push(`${handleName}: ${error}`);
                        }
                      }
                    }
                  }
                } catch {  // ParseError
                  errors.push(gqlError.message);
                }
              }

            }
          } finally {
            this.$data.submitting = false;
          }

        }
      },
      async next() {
        if (await this.validate() && !this.isLastPage) {
          const currentIndex = this.enabledPages.findIndex(page => page.index! == this.currentPage);
          this.currentPage = this.enabledPages[currentIndex + 1].index!;
        } else {
          // todo: it might be a good idea to scroll to the erroring field or
          // popup a dialog
        }
      },
      back() {
        if (!this.isFirstPage) {
          const currentIndex = this.enabledPages.findIndex(page => page.index! == this.currentPage);
          this.currentPage = this.enabledPages[currentIndex - 1].index!;
        }
      },
      async save() {
        // not supported in freeform's graphql, we would have to do it ourselves
      }
    }
  };
</script>

<template>
  <section>
    <Form class="form" ref="formComponent"
      :validate-on-value-update="false" :validate-on-blur="false"
      :validate-on-mount="false" :validate-on-submit="false">
      <Stepper :value="successPage ? _form.pages!.length + 1 : currentPage + 1"
        @update:value="page => { if (!successPage) { currentPage = page - 1; } }">
        <StepList class="step-list" v-if="_form.pages!.length > 1">
          <Step v-for="page in _form.pages?.filter(page => isPageEnabled(page!.index!))"
            :value="page!.index! + 1"
            :disabled="page!.index! > currentPage || successPage || submitting">{{ page!.label }}</Step>
          <Step :value="_form!.pages!.length + 1" disabled>Submitted</Step>
        </StepList>

        <StepPanels>
          <StepPanel v-for="{page} in pageEntries()" :value="page!.index! + 1">
            <div class="field-grid">
              <div v-for="{row, field, column_i} in rowFieldEntries(page!)"
                :class="{
                  'field': true,
                  'field-row-start': row != null && column_i == 0,
                  'field-row-end': row != null && column_i == row!.fields!.length - 1,
                  'field-hidden': row == null,
                  'field-disabled': !isFieldEnabled(field, page!.index!),
                }">
                <FormField v-if="field.type! !== 'rich-text' && field.type! !== 'html'"
                  :field="field!"
                  :site="site"
                  :initial-value="initialValues?.[field.handle!] ?? undefined"
                  :disabled="submitting"/>
                <Prose v-if="field.type! === 'rich-text' || field.type! === 'html'" class="deep">
                  <div v-html="(field as (FreeformField_RichText | FreeformField_Html)).content"></div>
                </Prose>
              </div>
            </div>

            <ButtonGroup class="button-row">
              <Button v-if="page!.buttons?.back === true && !isFirstPage"
                :label="page!.buttons?.backLabel ?? 'Back'"
                :disabled="submitting"
                @click.prevent="back">
                <Icon :icon="faArrowLeft"/>
                <span class="p-button-label" data-pc-section="label">
                  {{ page!.buttons?.backLabel ?? 'Back' }}
                </span>
              </Button>

              <!-- not supported with graphql -->
              <!-- <Button v-if="page!.buttons?.save === true || true"
                :label="page!.buttons?.saveLabel ?? 'Save'" icon-pos="right"
                @click.prevent="save">
                <template #icon="slot">
                  <Icon :class="[slot.class]" :icon="faFloppyDisk"/>
              </Button> -->

              <Button v-if="!isLastPage"
                :label="page!.buttons?.submitLabel ?? 'Next'" icon-pos="right"
                :disabled="pageHasErrors || submitting"
                @click.prevent="next">
                <template #icon="slot">
                  <Icon :class="[slot.class]" :icon="faArrowRight"/>
                </template>
              </Button>

              <Button v-if="isLastPage"
                :label="page!.buttons?.submitLabel ?? 'Submit'" icon-pos="right"
                :disabled="pageHasErrors || submitting"
                @click.prevent="submit"
                :loading="submitting">
                <template #icon="slot">
                  <Icon :class="[slot.class]" :icon="submitting ? faCircleNotch : faArrowRight" :spin="submitting"/>
                </template>
              </Button>
            </ButtonGroup>

            <Message v-for="error in submitErrors" severity="error" size="small" variant="simple">
              {{ error }}
            </Message>
          </StepPanel>

          <StepPanel :value="_form!.pages!.length + 1">
            <Prose class="submit-success">
              <slot name="successMessage" :message="_form.successMessage">
                <div v-html="_form.successMessage"></div>
              </slot>
            </Prose>

            <Icon class="submit-success-icon" :icon="faCircleCheck" size="5x"/>
          </StepPanel>
        </StepPanels>
      </Stepper>
    </Form>
  </section>
</template>

<style lang="less" scoped>
  .form {
    --p-stepper-steppanel-background: transparent;
  }

  .field-grid {
    display: grid;
    grid: auto-flow / 1fr 1fr;
    width: 100%;

    margin-block: var(--spacing-block);
    gap: var(--spacing-block);

    align-items: flex-start;
    justify-items: stretch;

    & > .field-row-start { grid-column-start: 1; }
    & > .field-row-end { grid-column-end: -1; }
    & > .field-hidden, & > .field-disabled { display: none; }
  }

  .button-row {
    display: flex;
    justify-content: center;
    margin-block: var(--spacing-block);
  }

  .submit-success {
    text-align-last: center;
    padding-block: var(--spacing-block);
  }

  .submit-success-icon {
    color: var(--p-message-success-color);
    --fa-width: 100%;
  }
</style>
